import { useEffect, useMemo, useState } from "react";
import Pagination from "../../Component/Common/Pagination";
import RefreshButton from "../../Component/Common/RefreshButton";
import formatCurrency from "../../Component/EmployeeComponents/formatCurrency";
import { BASE_URL } from "../../config/api";

export default function ReportsTable() {

  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
  const position = authUser?.position;
  const userId = authUser?.id;

  const isManager = position === "MANAGER";
  const iscompany = authUser?.role === 2; 

  const [activeTab, setActiveTab] = useState("self"); // self | team
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;

  /* ================= API URL ================= */
  const getReportUrl = () => {

    if (authUser?.role === 2) {

      return activeTab === "team"
        ? `${BASE_URL}warranty/wlead/report?company_id=${authUser.id}`
        : `${BASE_URL}warranty/wlead/report?created_by_id=${userId}`;
    }
    if (isManager) {
      return activeTab === "team"
        ? `${BASE_URL}warranty/wlead/report?manager_id=${userId}`
        : `${BASE_URL}warranty/wlead/report?created_by_id=${userId}`;
    }

    // executive
    return `${BASE_URL}warranty/wlead/report?created_by_id=${userId}`;
  };

  /* ================= FETCH REPORT ================= */
  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await fetch(getReportUrl());
      const data = await res.json();

      if (!res.ok || !data.status) {
        throw new Error(data.message || "Failed to fetch report");
      }

      const mapped = (data.data || []).map((item, index) => ({
        index,
        label: `${item.month_name} ${item.year}`,
        totalLeads: item.total_leads,
        wonLeads: item.status_counts?.won || 0,
        revenue: item.status_counts?.won
          ? item.total_lead_amount
          : 0,
        conversionRate: item.conversion_rate,
      }));

      setRows(mapped);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [activeTab]);

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(rows.length / itemsPerPage);

  const paginatedRows = useMemo(
    () =>
      rows.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      ),
    [rows, currentPage]
  );

  /* ================= UI ================= */
  return (
    <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4 xl:min-h-[540px]">

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-900 text-sm md:text-base">
          Monthly Report
        </h2>

        <div className="flex gap-2">
          <span className="text-[11px] px-2 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-100">
            Leads & Revenue
          </span>
          <RefreshButton onClick={fetchReport} />
        </div>
      </div>

      {/* Tabs (Manager only) */}
      {(isManager || iscompany) && (
        <div className="flex gap-2 mb-3">
          {[
            { key: "self", label: "Self Monthly Report" },
            { key: "team", label: "Team Monthly Report" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-xs rounded-full border transition
                ${
                  activeTab === tab.key
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* ================= DATA VIEW ================= */}
      <div className="mt-2">

        {/* ================= MOBILE CARD VIEW ================= */}
        <div className="md:hidden space-y-4">
          {loading ? (
            <div className="py-8 text-center text-slate-400 text-sm">
              Loading report…
            </div>
          ) : paginatedRows.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-sm">
              No report data available
            </div>
          ) : (
            paginatedRows.map((m) => (
              <div
                key={m.index}
                className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm"
              >
                {/* Header */}
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-semibold text-slate-800">
                    {m.label}
                  </h3>

                  <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-600">
                    {m.conversionRate}
                  </span>
                </div>

                {/* Divider */}
                <div className="h-px bg-slate-100 mb-3" />

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-y-3 text-xs">

                  <div>
                    <p className="text-slate-400">Total Leads</p>
                    <p className="font-semibold text-slate-900 text-sm">
                      {m.totalLeads}
                    </p>
                  </div>

                  <div>
                    <p className="text-slate-400">Won Leads</p>
                    <p className="font-semibold text-green-600 text-sm">
                      {m.wonLeads}
                    </p>
                  </div>

                  <div className="col-span-2">
                    <p className="text-slate-400">Won Revenue</p>
                    <p className="font-semibold text-slate-900 text-base">
                      {formatCurrency(m.revenue)}
                    </p>
                  </div>

                </div>
              </div>
            ))
          )}
        </div>

        {/* ================= DESKTOP TABLE ================= */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full text-left text-xs md:text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="py-2 px-2 font-medium text-slate-500">Month</th>
                <th className="py-2 px-2 font-medium text-slate-500">Total Leads</th>
                <th className="py-2 px-2 font-medium text-slate-500">Won Leads</th>
                <th className="py-2 px-2 font-medium text-slate-500">Win Rate</th>
                <th className="py-2 px-2 font-medium text-slate-500">
                  Won Revenue
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="py-6 text-center text-slate-400">
                    Loading report…
                  </td>
                </tr>
              ) : paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-6 text-center text-slate-400">
                    No report data available
                  </td>
                </tr>
              ) : (
                paginatedRows.map((m) => (
                  <tr
                    key={m.index}
                    className="border-b border-slate-50 last:border-0"
                  >
                    <td className="py-2 px-2 text-slate-900">{m.label}</td>
                    <td className="py-2 px-2 text-slate-600">{m.totalLeads}</td>
                    <td className="py-2 px-2 text-slate-600">{m.wonLeads}</td>
                    <td className="py-2 px-2 text-slate-600">
                      {m.conversionRate}
                    </td>
                    <td className="py-2 px-2 text-slate-900">
                      {formatCurrency(m.revenue)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-3 flex justify-end">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
}


// import { useEffect, useMemo, useState } from "react";
// import Pagination from "../../Component/Common/Pagination";
// import RefreshButton from "../../Component/Common/RefreshButton";
// import formatCurrency from "../../Component/EmployeeComponents/formatCurrency";
// import { BASE_URL } from "../../config/api";

// export default function ReportsTable() {
//   const [rows, setRows] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 4;

//   /* ================= FETCH REPORT ================= */
//   const fetchReport = async () => {
//     setLoading(true);
//     try {
//       const res = await fetch(`${BASE_URL}warranty/wlead/report`);
//       const data = await res.json();

//       if (!res.ok || !data.status) {
//         throw new Error(data.message || "Failed to fetch report");
//       }

//       // 🔁 normalize API response → table shape
//       const mapped = (data.data || []).map((item, index) => ({
//         index,
//         label: `${item.month_name} ${item.year}`,
//         totalLeads: item.total_leads,
//         wonLeads: item.status_counts?.won || 0,
//         revenue: item.status_counts?.won
//           ? item.total_lead_amount
//           : 0,
//         conversionRate: item.conversion_rate,
//       }));

//       setRows(mapped);
//     } catch (err) {
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchReport();
//   }, []);

//   /* ================= PAGINATION ================= */
//   const totalPages = Math.ceil(rows.length / itemsPerPage);

//   const paginatedRows = useMemo(
//     () =>
//       rows.slice(
//         (currentPage - 1) * itemsPerPage,
//         currentPage * itemsPerPage
//       ),
//     [rows, currentPage]
//   );

//   /* ================= UI ================= */
//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4 mt-3 xl:min-h-[540px]">

//       {/* Header */}
//       <div className="flex items-center justify-between mb-3">
//         <h2 className="font-semibold text-slate-900 text-sm md:text-base">
//           Monthly Report
//         </h2>

//         <div className="flex gap-2">
//           <span className="text-[11px] px-2 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-100">
//             Leads &amp; Revenue
//           </span>
//           <RefreshButton onClick={fetchReport} />
//         </div>
//       </div>

//       {/* Table */}
//       <div className="overflow-x-auto">
//         <table className="min-w-full text-left text-xs md:text-sm">
//           <thead>
//             <tr className="border-b border-slate-100 bg-slate-50">
//               <th className="py-2 px-2 font-medium text-slate-500">Month</th>
//               <th className="py-2 px-2 font-medium text-slate-500">Total Leads</th>
//               <th className="py-2 px-2 font-medium text-slate-500">Won Leads</th>
//               <th className="py-2 px-2 font-medium text-slate-500">Win Rate</th>
//               <th className="py-2 px-2 font-medium text-slate-500">Won Revenue</th>
//             </tr>
//           </thead>

//           <tbody>
//             {loading ? (
//               <tr>
//                 <td colSpan="5" className="py-6 text-center text-slate-400">
//                   Loading report…
//                 </td>
//               </tr>
//             ) : paginatedRows.length === 0 ? (
//               <tr>
//                 <td colSpan="5" className="py-6 text-center text-slate-400">
//                   No report data available
//                 </td>
//               </tr>
//             ) : (
//               paginatedRows.map((m) => (
//                 <tr
//                   key={m.index}
//                   className="border-b border-slate-50 last:border-0"
//                 >
//                   <td className="py-2 px-2 text-slate-900">{m.label}</td>
//                   <td className="py-2 px-2 text-slate-600">{m.totalLeads}</td>
//                   <td className="py-2 px-2 text-slate-600">{m.wonLeads}</td>
//                   <td className="py-2 px-2 text-slate-600">
//                     {m.conversionRate}
//                   </td>
//                   <td className="py-2 px-2 text-slate-900">
//                     {formatCurrency(m.revenue)}
//                   </td>
//                 </tr>
//               ))
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* Pagination */}
//       {totalPages > 1 && (
//         <div className="mt-3 flex justify-end">
//           <Pagination
//             currentPage={currentPage}
//             totalPages={totalPages}
//             onPageChange={setCurrentPage}
//           />
//         </div>
//       )}
//     </div>
//   );
// }



// import { useState } from "react";
// import Pagination from "../../Component/Common/Pagination";
// import RefreshButton from "../../Component/Common/RefreshButton";
// import computeMonthlyReport from "../../Component/EmployeeComponents/computeMonthlyReport";
// import formatCurrency from "../../Component/EmployeeComponents/formatCurrency";


// export default function ReportsTable({ leads }) {
//   const monthly = computeMonthlyReport(leads);
//   const nonEmpty = monthly.filter((m) => m.totalLeads > 0);
//   const rows = nonEmpty.length ? nonEmpty : monthly.slice(0, 6);

//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 4;

//   const totalPages = Math.ceil(rows.length / itemsPerPage);

//   const paginatedRows = rows.slice(
//     (currentPage - 1) * itemsPerPage,
//     currentPage * itemsPerPage
//   );

//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4 mt-3">

//       {/* Header */}
//       <div className="flex items-center justify-between mb-3">
//         <h2 className="font-semibold text-slate-900 text-sm md:text-base">
//           Monthly Report
//         </h2>

//         <div className="flex gap-2">
//           <span className="text-[11px] px-2 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-100">
//             Leads &amp; Revenue
//           </span>
//           <RefreshButton />
//         </div>
//       </div>

//       {/* Table */}
//       <div className="overflow-x-auto">
//         <table className="min-w-full text-left text-xs md:text-sm">
//           <thead>
//             <tr className="border-b border-slate-100 bg-slate-50">
//               <th className="py-2 px-2 font-medium text-slate-500">Month</th>
//               <th className="py-2 px-2 font-medium text-slate-500">Total Leads</th>
//               <th className="py-2 px-2 font-medium text-slate-500">Won Leads</th>
//               <th className="py-2 px-2 font-medium text-slate-500">Win Rate</th>
//               <th className="py-2 px-2 font-medium text-slate-500">Won Revenue</th>
//             </tr>
//           </thead>

//           <tbody>
//             {paginatedRows.map((m) => {
//               const winRate =
//                 m.totalLeads === 0
//                   ? "0%"
//                   : Math.round((m.wonLeads / m.totalLeads) * 100) + "%";

//               return (
//                 <tr key={m.index} className="border-b border-slate-50 last:border-0">
//                   <td className="py-2 px-2 text-slate-900">{m.label}</td>
//                   <td className="py-2 px-2 text-slate-600">{m.totalLeads}</td>
//                   <td className="py-2 px-2 text-slate-600">{m.wonLeads}</td>
//                   <td className="py-2 px-2 text-slate-600">{winRate}</td>
//                   <td className="py-2 px-2 text-slate-900">
//                     {formatCurrency(m.revenue)}
//                   </td>
//                 </tr>
//               );
//             })}
//           </tbody>
//         </table>
//       </div>

//       {/* Pagination */}
//       {totalPages > 1 && (
//         <div className="mt-3 flex justify-end">
//           <Pagination
//             currentPage={currentPage}
//             totalPages={totalPages}
//             onPageChange={setCurrentPage}
//           />
//         </div>
//       )}
//     </div>
//   );
// }



// import RefreshButton from "../../Component/Common/RefreshButton";
// import computeMonthlyReport from "../../Component/EmployeeComponents/computeMonthlyReport";
// import formatCurrency from "../../Component/EmployeeComponents/formatCurrency";

// export default function ReportsTable({ leads }) {
//   const monthly = computeMonthlyReport(leads);
//   const nonEmpty = monthly.filter((m) => m.totalLeads > 0);
//   const rows = nonEmpty.length ? nonEmpty : monthly.slice(0, 6);

//   return (
//     <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4">
//       <div className="flex items-center justify-between mb-3">
//         <h2 className="font-semibold text-slate-900 text-sm md:text-base">
//           Monthly Report
//         </h2>
//         <div className="flex gap-2">
//           <span className="text-[11px] px-2 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-100">
//           Leads &amp; Revenue
//         </span>
//         <RefreshButton/>
//         </div>
//       </div>
//       <div className="overflow-x-auto">
//         <table className="min-w-full text-left text-xs md:text-sm">
//           <thead>
//             <tr className="border-b border-slate-100 bg-slate-50">
//               <th className="py-2 px-2 font-medium text-slate-500">Month</th>
//               <th className="py-2 px-2 font-medium text-slate-500">Total Leads</th>
//               <th className="py-2 px-2 font-medium text-slate-500">Won Leads</th>
//               <th className="py-2 px-2 font-medium text-slate-500">Win Rate</th>
//               <th className="py-2 px-2 font-medium text-slate-500">Won Revenue</th>
//             </tr>
//           </thead>
//           <tbody>
//             {rows.map((m) => {
//               const winRate =
//                 m.totalLeads === 0
//                   ? "0%"
//                   : Math.round((m.wonLeads / m.totalLeads) * 100) + "%";
//               return (
//                 <tr
//                   key={m.index}
//                   className="border-b border-slate-50 last:border-0"
//                 >
//                   <td className="py-2 px-2 text-slate-900">{m.label}</td>
//                   <td className="py-2 px-2 text-slate-600">{m.totalLeads}</td>
//                   <td className="py-2 px-2 text-slate-600">{m.wonLeads}</td>
//                   <td className="py-2 px-2 text-slate-600">{winRate}</td>
//                   <td className="py-2 px-2 text-slate-900">
//                     {formatCurrency(m.revenue)}
//                   </td>
//                 </tr>
//               );
//             })}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// }